import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from '../baseStore';
import { RootStore } from '../rootStore';
import { StatementTransactionService } from '/src/api/services';
import { Statement, Transaction } from '/src/lib/models';
import { DataModelPluralType, TListStatementTransactionsRequest } from '/src/lib/types';

export interface StatementTransactionsStore {
  /**
   * List of Statement transactions
   */
  transactions?: Transaction[];
  /**
   * List of Statements
   */
  statements?: Statement[];
  /**
   * Transaction and statement totals
   */
  totals?: Record<string, number>;
  /**
   * Whether all Transactions in the list are Adjustments
   */
  allAdjustments?: boolean;
}

export class StatementTransactionsStore extends BaseStore {
  transactions?: Transaction[];
  statements?: Statement[];
  totals?: Record<string, number>;
  allAdjustments?: boolean;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      transactions: observable,
      statements: observable,
      totals: observable,
      allAdjustments: observable,
    });
  }

  /**** Data Processing ****/

  /**
   * Perform calculations and get Transaction totals.
   *
   * @param transactions - List of Transactions to total
   * @returns Transaction and Statement lists, totals, and related flags
   */
  private calculateTotals = (transactions: Transaction[]) => {
    const statements: Statement[] = [];
    const totals: Record<any, number> = {
      feesCharged: 0,
      feeTotal: 0,
      feesPaid: 0,
      interestCharged: 0,
      interestPaid: 0,
      principalPaid: 0,
      currentPrincipal: 0,
      advanceAmount: 0,
      adjustmentAmount: 0,
      paymentAmount: 0,
    };
    let allAdjustments = false;

    for (const transaction of transactions) {
      // Push all statement transactions to the Statements array
      if (transaction.__t === DataModelPluralType.Statement) statements.push(transaction as Statement);

      // Don't show adjustments
      if (transaction.__t !== DataModelPluralType.Adjustment) {
        // Set the 'list is all adjustments' flag to false
        allAdjustments = false;

        // Define some item-specific variables
        if (transaction.__t === DataModelPluralType.Advance) {
          transaction.advanceAmount = transaction.amount;
          totals.advanceAmount += transaction.amount;
        } else if (transaction.__t === 'payments') {
          transaction.paymentAmount = transaction.amount;
          totals.paymentAmount += transaction.amount;
        }

        // Fees due
        transaction.feesDue =
          (transaction.feeBalance ?? 0) +
          (transaction.originationFee ?? 0) +
          (transaction.diligenceFee ?? 0) +
          (transaction.lateFee ?? 0) +
          (transaction.facilityFee ?? 0) +
          (transaction.adminFee ?? 0);

        // Fees paid
        if (transaction.appliedToFees) {
          transaction.feesDue -= transaction.appliedToFees;

          totals.feesPaid += transaction.appliedToFees;
        }

        // Interest due
        transaction.interestDue = (transaction.interestBalance ?? 0) + (transaction.interestCharge ?? 0);

        // Interest paid
        if (transaction.appliedToInterest) {
          transaction.interestDue -= transaction.appliedToInterest;

          totals.interestPaid += transaction.appliedToInterest;
        }

        // Principal balance
        if (transaction.__t === DataModelPluralType.Advance) {
          transaction.newPrincipalBalance = (transaction.principalBalance ?? 0) + transaction.amount;

          totals.currentPrincipal = transaction.newPrincipalBalance;
        } else if (transaction.__t === DataModelPluralType.Payment) {
          transaction.newPrincipalBalance = (transaction.principalBalance ?? 0) - transaction.appliedToPrincipal;

          totals.currentPrincipal = transaction.newPrincipalBalance;
          totals.principalPaid += transaction.appliedToPrincipal;
        } else if (transaction.__t === DataModelPluralType.Adjustment)
          transaction.newPrincipalBalance = transaction.principalBalance ?? 0;
        totals.currentPrincipal = transaction.newPrincipalBalance ?? 0;
      }

      if (transactions && transactions[0]) {
        if (transactions[0].newPrincipalBalance) totals.currentPrincipal = transactions[0].newPrincipalBalance;
        if (transactions[0].assetValue) totals.currentAssets = transactions[0].assetValue;
        if (transactions[0].currentAvailableCredit)
          totals.currentAvailableCredit = transactions[0].currentAvailableCredit;
      }
    }

    return { transactions, statements, totals, allAdjustments };
  };

  /**** Statement Transactions CRUD ****/

  /**
   * Fetches and caches a list of Statements and their Transactions.
   *
   * @param filters - Filters to use when fetching Transactions
   * @returns Transaction and Statement lists, totals, and related flags
   */
  query = async (filters: TListStatementTransactionsRequest) => {
    if (!filters.loanId) return;

    // Execute the query and get the Transactions list
    const response = await StatementTransactionService.query(filters);

    const { transactions, statements, totals, allAdjustments } = this.calculateTotals(response.transactions);

    // Sort the history by date (newest at index 0)
    transactions.sort((a, b) => (b as any).sequenceNumber - (a as any).sequenceNumber);

    runInAction(() => {
      this.transactions = transactions;
      this.statements = statements;
      this.totals = totals;
      this.allAdjustments = allAdjustments;
    });
  };
}
